import React, { useEffect, useState } from 'react';
import { useCurrentTeam } from '../hooks/useCurrentTeam';
import Breadcrumb from '../components/Breadcrumb';
import { getPairStats, getPlayerStreaks } from '../api/stats';
import 'font-awesome/css/font-awesome.min.css';
import './PairStats.css';

const getInitials = (fullName = '') => {
    const parts = fullName.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
};

const getRating = (winRate, matchesTogether) => {
    if (matchesTogether === 0) return { label: 'Sin historial', cls: 'rating--none' };
    if (winRate >= 0.7)        return { label: 'Buena dupla',   cls: 'rating--good' };
    if (winRate >= 0.4)        return { label: 'Regular',       cls: 'rating--mid'  };
    return                            { label: 'Mejorable',     cls: 'rating--low'  };
};

export default function PairStats() {
    const { team } = useCurrentTeam();
    const [pairs, setPairs]               = useState([]);
    const [streaks, setStreaks]           = useState([]);
    const [loading, setLoading]           = useState(true);
    const [error, setError]               = useState(null);
    const [sortBy, setSortBy]             = useState('winRate');
    const [filter, setFilter]             = useState('all');
    const [streakOpen, setStreakOpen]     = useState(false);
    const [listOpen, setListOpen]         = useState(false);

    useEffect(() => {
        if (!team?.id) return;
        setLoading(true);
        Promise.all([
            getPairStats(team.id),
            getPlayerStreaks(team.id),
        ])
            .then(([pairsData, streaksData]) => {
                setPairs(pairsData || []);
                setStreaks(streaksData || []);
            })
            .catch(() => setError('No se pudieron cargar las estadísticas.'))
            .finally(() => setLoading(false));
    }, [team?.id]);

    const withHistory = pairs.filter(p => p.matchesTogether > 0);
    const topPairs    = [...withHistory]
        .sort((a, b) => {
            if (b.winRate !== a.winRate) return b.winRate - a.winRate;
            return b.matchesTogether - a.matchesTogether;
        })
        .slice(0, 3);

    const sorted = [...pairs]
        .filter(p => {
            if (filter === 'with')    return p.matchesTogether > 0;
            if (filter === 'without') return p.matchesTogether === 0;
            return true;
        })
        .sort((a, b) => {
            if (sortBy === 'winRate') {
                if (!a.matchesTogether && !b.matchesTogether) return 0;
                if (!a.matchesTogether) return 1;
                if (!b.matchesTogether) return -1;
                if (b.winRate !== a.winRate) return b.winRate - a.winRate;
                return b.matchesTogether - a.matchesTogether;
            }
            if (sortBy === 'matches') return b.matchesTogether - a.matchesTogether;
            return a.player1Name.localeCompare(b.player1Name);
        });

    const MEDALS = ['🥇', '🥈', '🥉'];

    return (
        <div className="pairstats-wrapper">
            <div className="pairstats-hero">
                <div className="pairstats-hero-top">
                    <Breadcrumb crumbs={[
                        { label: 'Inicio', to: '/dashboard' },
                        { label: 'Estadísticas de Parejas' },
                    ]} />
                </div>
                <div className="pairstats-hero-inner">
                    <span className="pairstats-eyebrow">Análisis de rendimiento</span>
                    <h1 className="pairstats-title">Compatibilidad de Parejas</h1>
                    <p className="pairstats-sub">Descubre qué combinaciones funcionan mejor en pista</p>
                </div>
            </div>

            <div className="pairstats-content">

                {topPairs.length > 0 && (
                    <section className="top-section">
                        <h2 className="section-title">
                            <i className="fa fa-trophy"></i> Mejores Duplas
                        </h2>
                        <div className="top-grid">
                            {topPairs.map((pair, i) => {
                                const pct = Math.round(pair.winRate * 100);
                                return (
                                    <div key={`top-${pair.player1Id}-${pair.player2Id}`} className={`top-card top-card--${i + 1}`}>
                                        <span className="top-medal">{MEDALS[i]}</span>
                                        <div className="top-avatars">
                                            <div className="top-avatar">{getInitials(pair.player1Name)}</div>
                                            <span className="top-plus">+</span>
                                            <div className="top-avatar top-avatar--b">{getInitials(pair.player2Name)}</div>
                                        </div>
                                        <div className="top-names">
                                            <span>{pair.player1Name.split(' ')[0]}</span>
                                            <span>{pair.player2Name.split(' ')[0]}</span>
                                        </div>
                                        <div className="top-pct">{pct}%</div>
                                        <div className="top-detail">{pair.wins}V · {pair.losses}D · {pair.matchesTogether} partidos</div>
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                )}

                {streaks.length > 0 && (
                    <section className="streak-section">
                        <button className="collapsible-header" onClick={() => setStreakOpen(o => !o)}>
                            <h2 className="section-title">
                                <i className="fa fa-fire"></i> Racha Actual
                            </h2>
                            <i className={`fa fa-chevron-down collapsible-chevron ${streakOpen ? 'collapsible-chevron--open' : ''}`}></i>
                        </button>
                        {streakOpen && (
                            <div className="streak-list">
                                {streaks.map(s => {
                                    const isWin = s.streakType === 'WIN';
                                    return (
                                        <div key={s.playerId} className={`streak-card ${isWin ? 'streak-card--win' : 'streak-card--loss'}`}>
                                            <div className="streak-avatar">{getInitials(s.playerName)}</div>
                                            <div className="streak-info">
                                                <span className="streak-name">{s.playerName}</span>
                                                <span className="streak-label">
                                                    {isWin ? '🔥' : '📉'} {s.streakCount} {s.streakCount === 1 ? 'partido' : 'partidos'} seguidos {isWin ? 'ganando' : 'perdiendo'}
                                                </span>
                                            </div>
                                            <span className={`streak-badge ${isWin ? 'streak-badge--win' : 'streak-badge--loss'}`}>
                                                {isWin ? `+${s.streakCount}` : `-${s.streakCount}`}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </section>
                )}

                <section className="list-section">
                    <div className="list-header collapsible-header" onClick={() => setListOpen(o => !o)} style={{ cursor: 'pointer' }}>
                        <h2 className="section-title">
                            <i className="fa fa-list"></i> Combinaciones
                        </h2>
                        <div className="list-header-right">
                            {listOpen && (
                                <div className="list-controls" onClick={e => e.stopPropagation()}>
                                    <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
                                        <option value="winRate">Ordenar: % Victorias</option>
                                        <option value="matches">Ordenar: Partidos</option>
                                        <option value="name">Ordenar: Nombre</option>
                                    </select>
                                    <select value={filter} onChange={e => setFilter(e.target.value)}>
                                        <option value="all">Todas</option>
                                        <option value="with">Con historial</option>
                                        <option value="without">Sin historial</option>
                                    </select>
                                </div>
                            )}
                            <i className={`fa fa-chevron-down collapsible-chevron ${listOpen ? 'collapsible-chevron--open' : ''}`}></i>
                        </div>
                    </div>

                    {listOpen && (loading ? (
                        <div className="pairstats-loading">
                            <i className="fa fa-spinner fa-spin fa-2x"></i>
                            <p>Cargando estadísticas...</p>
                        </div>
                    ) : error ? (
                        <div className="pairstats-error">
                            <i className="fa fa-exclamation-circle"></i> {error}
                        </div>
                    ) : sorted.length === 0 ? (
                        <div className="pairstats-empty">
                            <i className="fa fa-users fa-3x"></i>
                            <p>No hay datos disponibles todavía.</p>
                        </div>
                    ) : (
                        <div className="pairs-list">
                            {sorted.map(pair => {
                                const pct    = pair.matchesTogether > 0 ? Math.round(pair.winRate * 100) : null;
                                const rating = getRating(pair.winRate, pair.matchesTogether);
                                return (
                                    <div key={`${pair.player1Id}-${pair.player2Id}`} className="pair-card">
                                        <div className="pair-players">
                                            <div className="pair-avatar">{getInitials(pair.player1Name)}</div>
                                            <div className="pair-names">
                                                <span>{pair.player1Name}</span>
                                                <span>{pair.player2Name}</span>
                                            </div>
                                            <div className="pair-avatar pair-avatar--b">{getInitials(pair.player2Name)}</div>
                                        </div>

                                        {pct !== null ? (
                                            <div className="pair-data">
                                                <div className="pair-counters">
                                                    <div className="pair-counter">
                                                        <span className="counter-val">{pair.matchesTogether}</span>
                                                        <span className="counter-lbl">partidos</span>
                                                    </div>
                                                    <div className="pair-counter pair-counter--win">
                                                        <span className="counter-val">{pair.wins}</span>
                                                        <span className="counter-lbl">victorias</span>
                                                    </div>
                                                    <div className="pair-counter pair-counter--loss">
                                                        <span className="counter-val">{pair.losses}</span>
                                                        <span className="counter-lbl">derrotas</span>
                                                    </div>
                                                </div>
                                                <div className="winrate-row">
                                                    <div className="winrate-bar">
                                                        <div
                                                            className={`winrate-fill ${rating.cls}`}
                                                            style={{ width: `${pct}%` }}
                                                        ></div>
                                                    </div>
                                                    <span className="winrate-pct">{pct}%</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="pair-no-history">
                                                <i className="fa fa-clock-o"></i>
                                                <span>Sin historial juntas</span>
                                            </div>
                                        )}

                                        <span className={`pair-rating ${rating.cls}`}>{rating.label}</span>
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </section>
            </div>
        </div>
    );
}
